import {
  FC,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
  ComponentProps,
} from 'react';
import intl from 'react-intl-universal';
import debounce from 'lodash/debounce';
import { TableOperationHeader } from '@getsynapse/design-system';
import FilterButton from 'Organisms/TableHeaderActionButtons/FilterButton';
import ExportProjectsButton from 'Pages/ProjectsListPage/components/ActiveProjects/components/ExportProjectsButton/ExportProjectsButton';

type TableOperationHeaderProps = ComponentProps<typeof TableOperationHeader>;

interface ProjectsTableOperationHeaderProps {
  tableName: string;
  toggleSidePanelFilters: () => void;
  onSearchChange: (value: string) => void;
  canFilter?: boolean;
  appliedSearchValue?: string;
  quickFilterButtons?: TableOperationHeaderProps['quickFilterButtons'];
}

const ProjectsTableOperationHeader: FC<ProjectsTableOperationHeaderProps> = ({
  tableName,
  onSearchChange,
  toggleSidePanelFilters,
  canFilter,
  appliedSearchValue,
  quickFilterButtons,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState<string>(
    () => appliedSearchValue || ''
  );

  const debouncedSearchChange = useMemo(() => {
    return debounce((value: string) => {
      onSearchChange(value);
    }, 500);
  }, [onSearchChange]);

  // Cleanup debounced function to prevent execution after component unmount
  useEffect(() => {
    if (searchInputRef.current && appliedSearchValue) {
      searchInputRef.current.focus();
    }
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange, appliedSearchValue]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearchChange(value);
    },
    [debouncedSearchChange]
  );

  return (
    <TableOperationHeader
      searchProps={{
        placeholder: intl.get('SEARCH_PLACEHOLDER'),
        ref: searchInputRef,
        'aria-label': `${tableName}__search-input`,
      }}
      inputValue={searchValue}
      setInputValue={handleSearchChange}
      quickFilterButtons={quickFilterButtons}
      actionButtons={
        <>
          {canFilter && (
            <FilterButton
              aria-label={`${tableName}__filter-button`}
              onToggleFilter={toggleSidePanelFilters}
            />
          )}
          <ExportProjectsButton />
        </>
      }
    />
  );
};

export default ProjectsTableOperationHeader;
